import {
  type QueryClient,
  queryOptions,
  useQuery,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/core/api";
import { RUN_POLL_INTERVAL_MS } from "@/profitability/constants";
import { reportKey, reportOutputKey } from "@/profitability/query";
import { reportsPath } from "@/profitability/report-query";
import type { ReportOutput } from "@/profitability/types";
import type { ReportOverview } from "./types";

/** One period's overview request, for `useQuery` or `useQueries`. */
export const reportOverviewOptions = (
  api: AxiosInstance,
  businessId: string,
  reportId: string,
  runId?: string | null,
  compare?: string | null,
) =>
  queryOptions({
    queryKey: [
      ...reportOverviewsKey(businessId, reportId),
      runId ?? null,
      compare ?? null,
    ],
    queryFn: ({ signal }) =>
      api
        .get<{
          overview: ReportOverview;
        }>(`${reportsPath(businessId, reportId)}/overview`, {
          signal,
          params: {
            ...(runId ? { run_id: runId } : {}),
            ...(compare ? { compare } : {}),
          },
        })
        .then((res) => res.data.overview),
    refetchInterval: (query) =>
      query.state.data?.running ? RUN_POLL_INTERVAL_MS : false,
    retry: false,
    enabled: !!reportId,
  });

/**
 * A period of the template as its Review shows it: the latest, or the one
 * `runId` stands for. Polls while a newer run is building, so the page moves
 * to it once it lands.
 */
export const useReportOverview = (
  businessId: string,
  reportId: string,
  runId?: string | null,
  /** What to compare with: "average_4", "last_year", "run:<id>"; last period when null. */
  compare?: string | null,
  /** False for a page that may have no template to ask (a draft's sample). */
  enabled = true,
  /** Shown while this period loads, such as another period's (the nav column). */
  placeholderData?: () => ReportOverview | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...reportOverviewOptions(api, businessId, reportId, runId, compare),
    enabled: enabled && !!reportId,
    placeholderData,
  });
};

/** Prefix of every overview of one template, whatever its period. */
export const reportOverviewsKey = (businessId: string, reportId: string) => [
  ...reportKey(businessId),
  "overview",
  reportId,
];

/** Overviews of one agent already in the cache, newest write first. */
const cachedOverviews = (
  client: QueryClient,
  businessId: string,
  reportId: string,
): ReportOverview[] =>
  client
    .getQueryCache()
    .findAll({ queryKey: reportOverviewsKey(businessId, reportId) })
    .filter((query) => query.state.data !== undefined)
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
    .flatMap((query) => {
      const data = client.getQueryData<ReportOverview>(query.queryKey);
      return data ? [data] : [];
    });

/** The newest cached overview, to stand in while another period loads. */
export const latestCachedOverview = (
  client: QueryClient,
  businessId: string,
  reportId: string,
): ReportOverview | undefined =>
  cachedOverviews(client, businessId, reportId)[0];

/**
 * An output already cached for one of the agent's periods, preferring the
 * period whose overview was written last. The one just asked for may not
 * have its own yet.
 */
export const cachedPeriodOutput = (
  client: QueryClient,
  businessId: string,
  reportId: string,
): ReportOutput | undefined => {
  for (const overview of cachedOverviews(client, businessId, reportId)) {
    const run = overview.run;
    if (!run?.interactive_available) continue;
    const data = client.getQueryData<ReportOutput>(
      reportOutputKey(businessId, run.id),
    );
    if (data) return data;
  }
  return undefined;
};
